import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Bordered highlight box for surfacing subscription lifecycle messages
 * inside containing cards. Two visual tones map to the existing
 * semantic-color palette:
 *
 *   - `'warning'` — Pending-Cancel + Pending-Plan-Change states.
 *                   `bg-dream-gold/15` + `border-dream-gold/30`
 *                   + `text-warning-text`
 *   - `'error'`   — Past-Due state. `bg-error/10` +
 *                   `border-error/30` + `text-error-text`
 *
 * Mockup source: `pwa/context/mockups/dreamable_ch2_account_states_modals.html`
 * `.warn` (line 14) + `.err` (line 15).
 *
 * Extracted in Batch 9A Commit 3a from two inline call-sites that
 * appeared in Commits 2 + 3:
 *   1. `PlanStatusCard.tsx` (Commit 2) — wraps the subtitle when
 *      `tone='warning' | 'error'`. Used by Start-Page + Account-A-01.
 *   2. `routes/account/SubscriptionRoute.tsx` (Commit 3) —
 *      A-02 SubscriptionSummaryCard's lifecycle highlight.
 *
 * Both sites carried the identical class soup; this extraction
 * preserves visual treatment 1:1 (refactor is behavior-neutral) and
 * sets up future delete-confirm / past-due-banner consumers (Batch
 * 9B/9C candidates).
 *
 * The `--color-error-text` token landed in Commit 2 specifically for
 * this box's text colour; `--color-warning-text` was pre-existing.
 */
const lifecycleHighlightBoxVariants = cva('w-full rounded-lg border p-3 text-xs leading-5', {
  variants: {
    tone: {
      warning: 'border-dream-gold/30 bg-dream-gold/15 text-warning-text',
      error: 'border-error/30 bg-error/10 text-error-text',
    },
  },
});

type Tone = NonNullable<VariantProps<typeof lifecycleHighlightBoxVariants>['tone']>;

export interface LifecycleHighlightBoxProps {
  tone: Tone;
  children: React.ReactNode;
  className?: string;
  /**
   * Raw `data-testid` value — caller controls the full selector. Two
   * existing call-sites use different prefixes
   * (`plan-status-highlight-{tone}` from Commit 2 and
   * `subscription-highlight-{suffix}` from Commit 3) so this component
   * stays prefix-agnostic; the caller composes the final string.
   */
  testId?: string;
}

export function LifecycleHighlightBox({
  tone,
  children,
  className,
  testId,
}: LifecycleHighlightBoxProps) {
  return (
    <div className={cn(lifecycleHighlightBoxVariants({ tone }), className)} data-testid={testId}>
      {children}
    </div>
  );
}
